import { useEffect, useRef, useState } from 'react'
import { getUser, createUser, updateUser, listRoleOptions, type Role } from '../api'
import { useTabs, useDataVersion, useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useEditLock } from '../layout/useEditLock'
import PasswordStrengthMeter, { isPasswordAcceptable } from '../components/PasswordStrengthMeter'
import PasswordInput from '../components/PasswordInput'
import { checkBeforeSave } from '../forms/checkForm'
import { fill, PAGES_USER_FORM_TEXTS as T } from '../../shared/texts'

const empty = { name: '', email: '', role: '', active: true, password: '' }

export default function UserForm({ tabId, id }: { tabId?: string, id?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { publishChange, closeTab } = useTabs()
    const { toast } = useToast()
    const { user: me, can } = useAuth()
    const isSelf = !!id && me?._id === id // editing your own account
    const readOnly = !can('users', 'write') // role lacks users:write → view only
    // Edit lock keyed by the user record ('user:<id>') — shared with the user's own
    // Profile page, so management-edit and self-edit lock each other out. The own
    // account is view-only here (isSelf), so it takes no lock (it would needlessly
    // block editing the own profile).
    const lock = useEditLock(id && !isSelf ? `user:${id}` : null)
    const locked = !lock.canEdit
    const grantedToasted = useRef(false)
    const recordVersion = useDataVersion(id ? `users:${id}` : '') // reload if another client saves it
    const [roles, setRoles] = useState<Role[]>([])
    const [form, setForm] = useState(empty)
    const [baseline, setBaseline] = useState(empty)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const dirty = JSON.stringify(form) !== JSON.stringify(baseline)

    // Notify (once) when the edit lock is handed over after waiting.
    useEffect(() => {
        if (lock.justGranted && !grantedToasted.current) {
            grantedToasted.current = true
            toast(T.canEditNow)
        }
    }, [lock.justGranted, toast])

    useEffect(() => {
        listRoleOptions().then((rs) => {
            setRoles(rs)
            // Default the role select to the first non-admin role for new users.
            if (!id) setForm((f) => (f.role ? f : { ...f, role: rs.find((r) => !r.isSystem)?.key ?? rs[0]?.key ?? '' }))
        }).catch(() => {})
    }, [id])

    useEffect(() => {
        if (!id) return
        getUser(id).then((u) => {
            const loaded = { name: u.name, email: u.email, role: u.role, active: u.active, password: '' }
            setForm(loaded); setBaseline(loaded)
        }).catch((e) => setError(e?.response?.data?.error ?? e?.message ?? T.loadError))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, recordVersion])

    const set = (patch: Partial<typeof empty>) => setForm((f) => ({ ...f, ...patch }))

    async function save(): Promise<SaveResult> {
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy(true); setError('')
        try {
            if (id) {
                await updateUser(id, {
                    name: form.name, role: form.role, active: form.active,
                    ...(form.password ? { password: form.password } : {}),
                })
                setBaseline({ ...form, password: '' }); setForm((f) => ({ ...f, password: '' }))
                toast(T.saved)
            } else {
                await createUser({ name: form.name, email: form.email, password: form.password, role: form.role })
                toast(T.created); closeTab('user-new')
            }
            publishChange('users', id)
            return { ok: true }
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(msg)
            return { ok: false, error: msg }
        } finally { setBusy(false) }
    }

    function submit(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !locked && !isSelf && !readOnly, save)

    // Password strength: required on create; on edit only if a new one is typed.
    const pwOk = id ? (!form.password || isPasswordAcceptable(form.password)) : isPasswordAcceptable(form.password)
    // Empty required fields no longer lock the button silently — the form check names them at the
    // field. A weak password still does: the strength meter under it says why.
    const canSubmit = dirty && !busy && !locked && !readOnly && (pwOk || !form.password)

    return (
        <form ref={formRef} noValidate onSubmit={submit} className="form-page">
            <div className="form-top">
                <div className="form-header">
                    <h1>{isSelf ? T.titleSelf : readOnly ? T.titleReadOnly : id ? T.titleEdit : T.titleNew}</h1>
                    {!isSelf && !readOnly && <button type="submit" disabled={!canSubmit}>{busy ? '…' : id ? T.apply : T.create}</button>}
                </div>
                {isSelf && <div className="notice">{T.selfNotice}</div>}
                {readOnly && !isSelf && <div className="notice">{T.readOnlyNotice}</div>}
                {locked && !isSelf && (
                    <div className="notice warn">
                        {fill(T.lockedQueue, { position: lock.position > 0 ? fill(T.lockedQueuePosition, { position: lock.position }) : '' })}
                    </div>
                )}
                {error && <div className="notice err">{error}</div>}
            </div>

            <fieldset disabled={busy || isSelf || locked || readOnly} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="panel">
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.name}</label>
                            <input type="text" value={form.name} required onChange={(e) => set({ name: e.target.value })} /></div>
                        <div className="field"><label>{T.email}</label>
                            <input type="email" value={form.email} required disabled={!!id}
                                onChange={(e) => set({ email: e.target.value })} />
                            {id && <span className="hint">{T.emailHint}</span>}</div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 220 }}><label>{T.role}</label>
                            <select value={form.role} onChange={(e) => set({ role: e.target.value })}>
                                {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                            </select></div>
                        <div className="field" style={{ alignSelf: 'flex-end' }}>
                            <label className="switch">
                                <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
                                {T.active}
                            </label>
                        </div>
                    </div>
                    <div className="field" style={{ maxWidth: 320 }}>
                        <label>{id ? T.newPasswordOptional : T.password}</label>
                        <PasswordInput value={form.password} required={!id} autoComplete="new-password"
                            placeholder={id ? T.passwordPlaceholder : ''}
                            onChange={(v) => set({ password: v })} />
                        <PasswordStrengthMeter password={form.password} />
                    </div>
                </div>
            </fieldset>
        </form>
    )
}
